import { formatDate, secondsSinceMidnightToTimeString } from '@/helpers/dateHelper';
import { getRequestStatus } from '@/helpers/requestStatusHelper';
import { StatusChip } from '@/pages/team/teamMember/requests/RequestStatusChip';
import { ApprovalState, AvailabilityExceptionRequest } from '@/services/gql/graphql.generated';
import { useLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { strings } from '@/services/translation/strings';
import { ReactNode } from 'react';

interface AvailabilityExceptionRequestDetailsCardProps {
  request: AvailabilityExceptionRequest;
  actions?: ReactNode;
}

const formatTime = (value: number) => secondsSinceMidnightToTimeString(value);
const formatRange = (start?: number | null, end?: number | null) =>
  start == null && end == null ? 'Not scheduled' : `${formatTime(start!)} - ${formatTime(end!)}`;
export const AvailabilityExceptionLeaveRequestDetailsCard = ({
  request,
  actions
}: AvailabilityExceptionRequestDetailsCardProps) => {
  const exception = request.exceptions?.[0];
  const status = getRequestStatus(request.requestState);

  const leaveLookup = useLeaveType(exception?.leaveType?.id);

  const decisionReason = request.rejectionReason?.trim();
  const isCancelledByManager = request.requestState === ApprovalState.CancelledByManager;
  const isRejected = request.requestState === ApprovalState.Rejected;
  const showReason = !!decisionReason && (isCancelledByManager || isRejected);
  const reasonLabel = isCancelledByManager ? 'Reason for cancellation' : 'Reason for denial';

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <p className="font-semibold">{strings.common.requestStatus}</p> <StatusChip status={status} />
        <p className="text-gray-400">{strings.common.requestSubmitted(formatDate(request.requestedOn))}</p>
      </div>
      {showReason && (
        <div>
          <p className="font-semibold">{reasonLabel}</p>
          <p className="whitespace-pre-wrap">{decisionReason}</p>
        </div>
      )}
      {exception && (
        <>
          {leaveLookup ? (
            <>
              <div>
                <p className="font-semibold">Leave Type</p> <p>{leaveLookup?.properties.displayName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">Start Date</p> <p>{formatDate(request.start)}</p>
                </div>
                <div>
                  <p className="font-semibold">End Date</p> <p>{formatDate(request.end)}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="font-semibold">Date To Adjust</p> <p>{formatDate(exception.date)}</p>
              </div>
              {exception.available ? (
                <>
                  <div>
                    <p className="font-semibold">Pattern Hours</p>
                    <p>{formatRange(exception.before?.start, exception.before?.end)}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Adjusted Hours</p>
                    <p>{formatRange(exception.start, exception.end)}</p>
                  </div>
                </>
              ) : (
                <div className="col-span-2">
                  <p className="font-semibold">Leave</p> <p>{exception.leaveType?.name ?? 'Leave'}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {actions}
    </div>
  );
};
